import {Injectable} from '@angular/core';
import {Subject, Observable} from 'rxjs';
import {MapPosition, Route} from '../route/metamodel';
import {AdditionalInfoForm} from './task-form/additional-info-form';
import {FuelingPoiEntry} from '../../../../core/component/fueling-poi/model/fueling-poi.entry';

export enum TaskDataTrigger {
  ORIGIN = 0,
  DESTINATION = 1,
  ROUTE = 2,
  FORM = 3,
  TRUCK_ROUTE = 4,
  ADDITIONAL = 5,
  REMOVE_ADDITIONAL = 6,
  QUEUE = 7
}

export interface TaskData {
  trigger: number;
  uuid: string;
  origin?: MapPosition;
  destination?: MapPosition;
  type?: string;
  truckRoute?: string;
  height?: number;
  width?: number;
  length?: number;
  weight?: number;
  avoids?: string[];
  strategy?: string;
  vehicleType?: string;
  waypoints?: MapPosition[];
  status?: string;
  finishedRoute?: string;
  idx?: number;
  additionalInfo?: AdditionalInfoForm[],
  distance?: number,
  duration?: number,
  tollsEntry?: any,
  alternatives?: any[],
  alternativesVisible?: boolean,
  additionalPoint?: boolean,
  fitBounds?: boolean,
  startedAt?: string
}

export interface TaskRoute {
  uuid: string;
  route: Route | string;
  truckRoute: string;
  status: string;
  updateAddress: boolean;
}

export interface TaskState {
  uuid: string;
  editable: boolean;
  additional?: string[];
}

@Injectable()
export class TasksManagerState {

  private taskDataSource = new Subject<TaskData>();
  private taskDatas$ = this.taskDataSource.asObservable();

  private taskRemovedSource = new Subject<string>();
  private taskRemoves$ = this.taskRemovedSource.asObservable();

  private routeChangedSource = new Subject<TaskRoute>();
  private routeChanged$ = this.routeChangedSource.asObservable();

  private taskStateSource = new Subject<TaskState>();
  private taskState$ = this.taskStateSource.asObservable();

  private calcTollsSource = new Subject<any>();
  private calcTolls$ = this.calcTollsSource.asObservable();

  private routeLoadedSource = new Subject<any>();
  private routeLoaded$ = this.routeLoadedSource.asObservable();

  private typeSelectedSource = new Subject<any>();
  private typeSelected$ = this.typeSelectedSource.asObservable();

  private insertSource = new Subject<any>();
  private insertions$ = this.insertSource.asObservable();

  private destinationSource = new Subject<any>();
  private destinations$ = this.destinationSource.asObservable();

  private activateTrackSource = new Subject<any>();
  private activateTrack$ = this.activateTrackSource.asObservable();

  private lastDoneTaskSource = new Subject<any>();
  private lastDoneTask$ = this.lastDoneTaskSource.asObservable();

  private showStationsSource = new Subject<FuelingPoiEntry[]>();
  private showStations$ = this.showStationsSource.asObservable();

  private removeStationsSource = new Subject<void>();
  private removeStations$ = this.removeStationsSource.asObservable();

  private labelsSource = new Subject<any>();
  private labels$ = this.labelsSource.asObservable();

  private viewportStateSource = new Subject<any>();
  private viewportState$ = this.viewportStateSource.asObservable();

  private alternativesSource = new Subject<any>();
  private alternatives$ = this.alternativesSource.asObservable();

  private calcTruckRouteSource = new Subject<any>();
  private calcTruckRoute$ = this.calcTruckRouteSource.asObservable();

  private redrawTachoLiveSource = new Subject<number>();
  private redrawTachoLive$ = this.redrawTachoLiveSource.asObservable();

  constructor() {
  }

  public taskData(data: TaskData): void {
    this.taskDataSource.next(data);
  }

  public taskDatas(): Observable<TaskData> {
    return this.taskDatas$;
  }

  public taskRemoved(uuid: string): void {
    this.taskRemovedSource.next(uuid);
  }

  public taskRemoves(): Observable<string> {
    return this.taskRemoves$;
  }

  public routeChanged(route: TaskRoute): void {
    this.routeChangedSource.next(route);
  }

  public routeChanges(): Observable<TaskRoute> {
    return this.routeChanged$;
  }

  public taskState(state: TaskState): void {
    this.taskStateSource.next(state);
  }

  public taskStates(): Observable<TaskState> {
    return this.taskState$;
  }

  public routeLoad(data: TaskData): void {
    this.routeLoadedSource.next(data);
  }

  public routeLoaded(): Observable<any> {
    return this.routeLoaded$;
  }

  public typeSelect(type: string): void {
    this.typeSelectedSource.next(type);
  }

  public typeSelected(): Observable<string> {
    return this.typeSelected$;
  }

  public insertion(data: any): void {
    this.insertSource.next(data);
  }

  public insertions(): Observable<any> {
    return this.insertions$;
  }

  public destination(data: any): void {
    this.destinationSource.next(data);
  }

  public destinations(): Observable<any> {
    return this.destinations$;
  }

  public activateTrack(data: any): void {
    this.activateTrackSource.next(data);
  }

  public activatingTrack(): Observable<any> {
    return this.activateTrack$;
  }

  public lastDoneTask(data: any): void {
    this.lastDoneTaskSource.next(data);
  }

  public receiveLastDoneTask(): Observable<any> {
    return this.lastDoneTask$;
  }

  public triggerShowStations(data: FuelingPoiEntry[]): void {
    this.showStationsSource.next(data);
  }

  public showStations(): Observable<FuelingPoiEntry[]> {
    return this.showStations$;
  }

  public triggerRemoveStations(): void {
    this.removeStationsSource.next();
  }

  public removeStations(): Observable<void> {
    return this.removeStations$;
  }

  public triggerRenameLabels(labels: Map<string, number>): void {
    this.labelsSource.next(labels);
  }

  public renameLabels(): Observable<Map<string, number>> {
    return this.labels$;
  }

  public triggerViewportState(state: boolean): void {
    this.viewportStateSource.next(state);
  }

  public viewportState(): Observable<boolean> {
    return this.viewportState$;
  }

  public triggerAlternatives(state: number): void {
    this.alternativesSource.next(state);
  }

  public alternatives(): Observable<number> {
    return this.alternatives$;
  }


  public triggerTruckRouteCalc(data: any): void {
    this.calcTruckRouteSource.next(data);
  }

  public calcTruckRoute(): Observable<any> {
    return this.calcTruckRoute$;
  }

  public triggerTachoRedraw(data: number) {
    this.redrawTachoLiveSource.next(data);
  }

  public redrawTacho(): Observable<number> {
    return this.redrawTachoLive$
  }
}
